/**
 * Тёмная тема витрины.
 *
 * `isDark` — прежний двусторонний computed поверх color-mode: присвоение любого
 * значения переключает тему. `toggleTheme(event)` — то же самое, но с круговой
 * заливкой от точки клика через View Transitions API.
 */

/** Длительность круговой заливки. Больше — уже не «отклик», а ожидание. */
const REVEAL_DURATION_MS = 420;

type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void | Promise<void>) => { ready: Promise<void> };
};

/**
 * Можно ли анимировать переход.
 *
 * View Transitions есть в Chromium и Safari 18, в Firefox — нет; там просто
 * мгновенная смена, как было раньше. `prefers-reduced-motion` — не вкусовая
 * настройка: полноэкранная заливка у части людей вызывает тошноту, и системный
 * флажок ровно об этом.
 */
function canAnimateTheme(): boolean {
  if (typeof document === 'undefined') return false;
  if (typeof (document as ViewTransitionDocument).startViewTransition !== 'function') return false;
  return !globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function useDarkMode() {
  const colorMode = useColorMode();
  const isDark = computed({
    get() {
      return colorMode.value === 'dark';
    },
    set() {
      colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark';
    },
  });

  /**
   * Переключение темы с круговой заливкой из точки клика.
   *
   * Радиус — расстояние до самого дальнего угла экрана, иначе круг не накроет
   * дальний край и в углу останется кусок старой темы.
   *
   * При включении тёмной темы анимируем СТАРЫЙ снимок (светлый круг схлопывается),
   * при выключении — НОВЫЙ (светлый круг раскрывается). Так растущий круг всегда
   * светлый: обратный порядок выглядит как чёрная клякса, ползущая по экрану.
   *
   * `event` необязателен: без него (клавиатура, программный вызов) заливка идёт
   * из центра экрана.
   */
  async function toggleTheme(event?: MouseEvent): Promise<void> {
    if (!canAnimateTheme()) {
      isDark.value = !isDark.value;
      return;
    }

    const x = event?.clientX ?? globalThis.innerWidth / 2;
    const y = event?.clientY ?? globalThis.innerHeight / 2;
    const endRadius = Math.hypot(
      Math.max(x, globalThis.innerWidth - x),
      Math.max(y, globalThis.innerHeight - y),
    );

    const transition = (document as ViewTransitionDocument).startViewTransition!(async () => {
      isDark.value = !isDark.value;
      await nextTick();
    });

    try {
      await transition.ready;
    } catch {
      // Переход отменён (успели кликнуть ещё раз) — тема уже переключена,
      // анимировать нечего
      return;
    }

    const clip = [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`];
    document.documentElement.animate(
      { clipPath: isDark.value ? [...clip].reverse() : clip },
      {
        duration: REVEAL_DURATION_MS,
        easing: 'ease-in-out',
        pseudoElement: isDark.value ? '::view-transition-old(root)' : '::view-transition-new(root)',
      },
    );
  }

  return {
    isDark,
    toggleTheme,
  };
}
